import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CIVIL_DOC_KINDS, isCivilDocType, civilDocShort } from "./civilDocs";
import {
  Trash2,
  Upload,
  FileText,
  FileUp,
  ChevronDown,
  Copy,
  Check,
  Edit2,
  X,
  Code,
  Globe,
  Lock,
  Braces,
  PenLine,
  CalendarDays,
  CaseSensitive,
  Lightbulb,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import PageHeader from "./PageHeader";
import { docTypeMeta } from "./docTypes";
import "./templates.css";

// бейдж типа документа в списке шаблонов: пастельная иконка + короткая подпись
const DocTypeBadge = ({ type }) => {
  const t = type || "passport";
  const meta = docTypeMeta(t);
  const { Icon } = meta;
  const label =
    t === "drivers_license" ? "ВУ" : isCivilDocType(t) ? civilDocShort(t) : "Паспорт";
  return (
    <span className="tpl-type">
      <span className={`tpl-type-icon tone-${meta.tone}`}>
        <Icon size={18} aria-hidden="true" />
      </span>
      {label}
    </span>
  );
};

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL !== undefined
    ? process.env.REACT_APP_API_BASE_URL
    : "http://localhost:5001";

const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { user } = useAuth();

  const [isPublic, setIsPublic] = useState(false);

  const navigate = useNavigate();

  const [showInstructions, setShowInstructions] = useState(false);
  const [showVariables, setShowVariables] = useState(false);

  const [uploadFile, setUploadFile] = useState(null);
  const [countryCode, setCountryCode] = useState("TJK");
  const [label, setLabel] = useState("");
  const [dateFormat, setDateFormat] = useState("DD.MM.YYYY");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [filterCountry, setFilterCountry] = useState("");
  const [selectedCountryForVars, setSelectedCountryForVars] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editDateFormat, setEditDateFormat] = useState("DD.MM.YYYY");

  const [variables, setVariables] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  //

  const [documentType, setDocumentType] = useState("passport");
  const [editDocType, setEditDocType] = useState("passport");
  const [filterDocType, setFilterDocType] = useState("");

  const countries = [
    { code: "AZE", name: "Азербайджан" },
    { code: "ALB", name: "Албания" },
    { code: "DZA", name: "Алжир" },
    { code: "AGO", name: "Ангола" },
    { code: "AND", name: "Андорра" },
    { code: "ATG", name: "Антигуа и Барбуда" },
    { code: "USA", name: "США" },
    { code: "ARG", name: "Аргентина" },
    { code: "ARM", name: "Армения" },
    { code: "AUS", name: "Австралия" },
    { code: "AUT", name: "Австрия" },
    { code: "AFG", name: "Афганистан" },

    { code: "BHS", name: "Багамы" },
    { code: "BHR", name: "Бахрейн" },
    { code: "BGD", name: "Бангладеш" },
    { code: "BRB", name: "Барбадос" },
    { code: "BLR", name: "Беларусь" },
    { code: "BLZ", name: "Белиз" },
    { code: "BEL", name: "Бельгия" },
    { code: "BEN", name: "Бенин" },
    { code: "BGR", name: "Болгария" },
    { code: "BOL", name: "Боливия" },
    { code: "BIH", name: "Босния и Герцеговина" },
    { code: "BWA", name: "Ботсвана" },
    { code: "BRA", name: "Бразилия" },
    { code: "BRN", name: "Бруней" },
    { code: "BFA", name: "Буркина-Фасо" },
    { code: "BDI", name: "Бурунди" },
    { code: "BTN", name: "Бутан" },

    { code: "VUT", name: "Вануату" },
    { code: "VAT", name: "Ватикан" },
    { code: "GBR", name: "Великобритания" },
    { code: "HUN", name: "Венгрия" },
    { code: "VEN", name: "Венесуэла" },
    { code: "VNM", name: "Вьетнам" },

    { code: "GAB", name: "Габон" },
    { code: "HTI", name: "Гаити" },
    { code: "GUY", name: "Гайана" },
    { code: "GMB", name: "Гамбия" },
    { code: "GHA", name: "Гана" },
    { code: "GTM", name: "Гватемала" },
    { code: "GIN", name: "Гвинея" },
    { code: "GNB", name: "Гвинея-Бисау" },
    { code: "DEU", name: "Германия" },
    { code: "HND", name: "Гондурас" },
    { code: "GRD", name: "Гренада" },
    { code: "GEO", name: "Грузия" },
    { code: "GRC", name: "Греция" },

    { code: "DNK", name: "Дания" },
    { code: "DJI", name: "Джибути" },
    { code: "DMA", name: "Доминика" },
    { code: "DOM", name: "Доминиканская Республика" },
    { code: "COD", name: "Демократическая Республика Конго" },

    { code: "EGY", name: "Египет" },

    { code: "ZMB", name: "Замбия" },
    { code: "ZWE", name: "Зимбабве" },

    { code: "ISR", name: "Израиль" },
    { code: "IND", name: "Индия" },
    { code: "IDN", name: "Индонезия" },
    { code: "JOR", name: "Иордания" },
    { code: "IRQ", name: "Ирак" },
    { code: "IRN", name: "Иран" },
    { code: "IRL", name: "Ирландия" },
    { code: "ISL", name: "Исландия" },
    { code: "ESP", name: "Испания" },
    { code: "ITA", name: "Италия" },

    { code: "YEM", name: "Йемен" },

    { code: "CPV", name: "Кабо-Верде" },
    { code: "KAZ", name: "Казахстан" },
    { code: "KHM", name: "Камбоджа" },
    { code: "CMR", name: "Камерун" },
    { code: "CAN", name: "Канада" },
    { code: "QAT", name: "Катар" },
    { code: "KEN", name: "Кения" },
    { code: "CYP", name: "Кипр" },
    { code: "KGZ", name: "Кыргызстан" },
    { code: "KIR", name: "Кирибати" },
    { code: "CHN", name: "Китай" },
    { code: "COL", name: "Колумбия" },
    { code: "COM", name: "Коморы" },
    { code: "COG", name: "Республика Конго" },
    { code: "CRI", name: "Коста-Рика" },
    { code: "CIV", name: "Кот-д'Ивуар" },
    { code: "CUB", name: "Куба" },
    { code: "KWT", name: "Кувейт" },
    { code: "PRK", name: "Северная Корея" },
    { code: "KOR", name: "Южная Корея" },

    { code: "LAO", name: "Лаос" },
    { code: "LVA", name: "Латвия" },
    { code: "LSO", name: "Лесото" },
    { code: "LBR", name: "Либерия" },
    { code: "LBN", name: "Ливан" },
    { code: "LBY", name: "Ливия" },
    { code: "LTU", name: "Литва" },
    { code: "LIE", name: "Лихтенштейн" },
    { code: "LUX", name: "Люксембург" },

    { code: "MUS", name: "Маврикий" },
    { code: "MRT", name: "Мавритания" },
    { code: "MDG", name: "Мадагаскар" },
    { code: "MKD", name: "Северная Македония" },
    { code: "MWI", name: "Малави" },
    { code: "MYS", name: "Малайзия" },
    { code: "MLI", name: "Мали" },
    { code: "MDV", name: "Мальдивы" },
    { code: "MLT", name: "Мальта" },
    { code: "MAR", name: "Марокко" },
    { code: "MHL", name: "Маршалловы Острова" },
    { code: "MEX", name: "Мексика" },
    { code: "FSM", name: "Микронезия" },
    { code: "MOZ", name: "Мозамбик" },
    { code: "MDA", name: "Молдова" },
    { code: "MCO", name: "Монако" },
    { code: "MNG", name: "Монголия" },
    { code: "MMR", name: "Мьянма" },

    { code: "NAM", name: "Намибия" },
    { code: "NRU", name: "Науру" },
    { code: "NPL", name: "Непал" },
    { code: "NER", name: "Нигер" },
    { code: "NGA", name: "Нигерия" },
    { code: "NLD", name: "Нидерланды" },
    { code: "NIC", name: "Никарагуа" },
    { code: "NZL", name: "Новая Зеландия" },
    { code: "NOR", name: "Норвегия" },

    { code: "ARE", name: "ОАЭ" },
    { code: "OMN", name: "Оман" },

    { code: "PAK", name: "Пакистан" },
    { code: "PLW", name: "Палау" },
    { code: "PSE", name: "Палестина" },
    { code: "PAN", name: "Панама" },
    { code: "PNG", name: "Папуа — Новая Гвинея" },
    { code: "PRY", name: "Парагвай" },
    { code: "PER", name: "Перу" },
    { code: "POL", name: "Польша" },
    { code: "PRT", name: "Португалия" },

    { code: "RUS", name: "Россия" },
    { code: "RWA", name: "Руанда" },
    { code: "ROU", name: "Румыния" },

    { code: "SLV", name: "Сальвадор" },
    { code: "WSM", name: "Самоа" },
    { code: "SMR", name: "Сан-Марино" },
    { code: "STP", name: "Сан-Томе и Принсипи" },
    { code: "SAU", name: "Саудовская Аравия" },
    { code: "SWZ", name: "Эсватини" },
    { code: "SYC", name: "Сейшелы" },
    { code: "SEN", name: "Сенегал" },
    { code: "VCT", name: "Сент-Винсент и Гренадины" },
    { code: "KNA", name: "Сент-Китс и Невис" },
    { code: "LCA", name: "Сент-Люсия" },
    { code: "SRB", name: "Сербия" },
    { code: "SGP", name: "Сингапур" },
    { code: "SYR", name: "Сирия" },
    { code: "SVK", name: "Словакия" },
    { code: "SVN", name: "Словения" },
    { code: "SLB", name: "Соломоновы Острова" },
    { code: "SOM", name: "Сомали" },
    { code: "SDN", name: "Судан" },
    { code: "SUR", name: "Суринам" },
    { code: "SLE", name: "Сьерра-Леоне" },

    { code: "TJK", name: "Таджикистан" },
    { code: "THA", name: "Таиланд" },
    { code: "TZA", name: "Танзания" },
    { code: "TGO", name: "Того" },
    { code: "TON", name: "Тонга" },
    { code: "TTO", name: "Тринидад и Тобаго" },
    { code: "TUV", name: "Тувалу" },
    { code: "TUN", name: "Тунис" },
    { code: "TKM", name: "Туркменистан" },
    { code: "TUR", name: "Турция" },

    { code: "UGA", name: "Уганда" },
    { code: "UZB", name: "Узбекистан" },
    { code: "UKR", name: "Украина" },
    { code: "URY", name: "Уругвай" },

    { code: "FJI", name: "Фиджи" },
    { code: "PHL", name: "Филиппины" },
    { code: "FIN", name: "Финляндия" },
    { code: "FRA", name: "Франция" },

    { code: "HRV", name: "Хорватия" },

    { code: "CAF", name: "Центральноафриканская Республика" },
    { code: "TCD", name: "Чад" },
    { code: "MNE", name: "Черногория" },
    { code: "CZE", name: "Чехия" },
    { code: "CHL", name: "Чили" },

    { code: "CHE", name: "Швейцария" },
    { code: "SWE", name: "Швеция" },
    { code: "LKA", name: "Шри-Ланка" },

    { code: "ECU", name: "Эквадор" },
    { code: "GNQ", name: "Экваториальная Гвинея" },
    { code: "ERI", name: "Эритрея" },
    { code: "EST", name: "Эстония" },
    { code: "ETH", name: "Эфиопия" },

    { code: "ZAF", name: "ЮАР" },
    { code: "SSD", name: "Южный Судан" },

    { code: "JAM", name: "Ямайка" },
    { code: "JPN", name: "Япония" },
  ].sort((a, b) => a.name.localeCompare(b.name, "ru"));

  const fetchUserTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const [userResp, publicResp] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/user-templates`),
        axios.get(`${API_BASE_URL}/api/user-templates/public`),
      ]);

      const userIds = new Set((userResp.data || []).map((t) => t.id));

      const userTpls = (userResp.data || []).map((t) => ({
        ...t,
        own: t.is_own ?? true,
      }));

      const publicTpls = (publicResp.data || [])
        .filter((t) => !userIds.has(t.id))
        .map((t) => ({ ...t, own: false }));

      setTemplates([...userTpls, ...publicTpls]);
    } catch (err) {
      setError("Не удалось загрузить шаблоны");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTogglePublic = async (id, currentIsPublic) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/user-templates/${id}`, {
        is_public: !currentIsPublic,
      });
      fetchUserTemplates();
    } catch (err) {
      alert("Ошибка при изменении публичности");
    }
  };

  const fetchVariables = useCallback(async (country, docType) => {
    try {
      const qs = new URLSearchParams();
      if (country) qs.set("country", country);
      // для документов РФ — переменные из реестра типов, а не паспортные
      if (isCivilDocType(docType)) qs.set("document_type", docType);
      const params = qs.toString() ? `?${qs}` : "";
      const response = await axios.get(
        `${API_BASE_URL}/api/templates/template-variables${params}`,
      );
      setVariables(response.data.categories);
    } catch (err) {
      console.error("Failed to load variables", err);
      setVariables({});
    }
  }, []);

  useEffect(() => {
    fetchUserTemplates();
  }, [fetchUserTemplates]);

  useEffect(() => {
    fetchVariables(selectedCountryForVars, documentType);
  }, [fetchVariables, selectedCountryForVars, documentType]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !label) {
      setUploadError("Заполните название и выберите файл");
      return;
    }
    if (!uploadFile.name.endsWith(".docx")) {
      setUploadError("Только .docx файлы");
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("country_code", countryCode);
      formData.append("label", label);
      formData.append("date_format", dateFormat);
      formData.append("is_public", isPublic.toString());
      formData.append("document_type", documentType);

      await axios.post(`${API_BASE_URL}/api/user-templates/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUploadFile(null);
      setLabel("");
      setDateFormat("DD.MM.YYYY");
      setIsPublic(false);
      fetchUserTemplates();
    } catch (err) {
      setUploadError(err.response?.data?.error || "Ошибка загрузки");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить этот шаблон?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/user-templates/${id}`);
      fetchUserTemplates();
    } catch (err) {
      alert("Ошибка удаления");
    }
  };

  const handleEdit = (template) => {
    setEditingId(template.id);
    setEditLabel(template.label);
    setEditDateFormat(template.date_format || "DD.MM.YYYY");
    setEditDocType(template.document_type || "passport");
  };

  const handleSaveEdit = async (id) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/user-templates/${id}`, {
        label: editLabel,
        date_format: editDateFormat,
        document_type: editDocType,
      });
      setEditingId(null);
      fetchUserTemplates();
    } catch (err) {
      alert("Ошибка при сохранении изменений");
    }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };


  // Кнопка «копировать» у плейсхолдера. Рендер-функции (а не вложенные
  // компоненты), чтобы при каждом рендере кнопки не пересоздавались и
  // клавиатурный фокус не терялся после копирования.
  const copyButton = (code, id, size = 16) => (
    <button
      type="button"
      className={`tpl-copy${copiedId === id ? " is-copied" : ""}`}
      onClick={() => handleCopy(code, id)}
      aria-label={copiedId === id ? `Скопировано: ${code}` : `Копировать ${code}`}
      title="Копировать"
    >
      {copiedId === id ? (
        <Check size={size} aria-hidden="true" />
      ) : (
        <Copy size={size} aria-hidden="true" />
      )}
    </button>
  );

  const placeholderRow = ({ code, label, id }) => (
    <div className="tpl-var" key={id}>
      <span className="tpl-var-code">
        <code>{code}</code>
        {copyButton(code, id, 14)}
      </span>
      <span className="tpl-var-label">{label}</span>
    </div>
  );

  const countryName = (code) =>
    countries.find((c) => c.code === code)?.name || code;

  // варианты подписей: form — в форме загрузки, filter — фильтр списка,
  // short — компактный селект в строке таблицы
  const docTypeOptions = (variant) => (
    <>
      <option value="passport">
        {variant === "filter" ? "Паспорта" : "Паспорт"}
      </option>
      <option value="drivers_license">
        {variant === "short"
          ? "ВУ"
          : variant === "filter"
            ? "Водительские удостоверения"
            : "Водительское удостоверение"}
      </option>
      <optgroup label="Документы РФ">
        {CIVIL_DOC_KINDS.map((k) => (
          <option key={k.key} value={k.key}>
            {variant === "short" ? k.short : k.label}
          </option>
        ))}
      </optgroup>
    </>
  );

  const countryOptions = countries.map((c) => (
    <option key={c.code} value={c.code}>
      {c.name}
    </option>
  ));

  const renderVariablesSection = () => {
    const loaded = variables && Object.keys(variables).length > 0;

    const head = (
      <>
        <span className="tpl-acc-icon tone-sky" aria-hidden="true">
          <Braces size={20} />
        </span>
        <span className="tpl-acc-text">
          <span className="res-eyebrow">Справка</span>
          <span id="tpl-vars-title" className="res-card-title">
            Доступные переменные для шаблонов
          </span>
        </span>
      </>
    );

    if (!loaded) {
      return (
        <section className="res-card tpl-acc" aria-labelledby="tpl-vars-title">
          <div className="tpl-acc-head">
            <div className="tpl-acc-toggle is-static">{head}</div>
          </div>
          <p className="tpl-muted tpl-acc-body">Загрузка переменных…</p>
        </section>
      );
    }

    const PASSPORT_HIDE = [
      "visas",
      "stamps",
      "registration_stamps",
      "dl_main",
      "dl_categories",
    ];
    const DL_SHOW = ["dl_main", "dl_categories"];

    const filteredVariables = Object.entries(variables).filter(
      ([categoryKey]) => {
        if (documentType === "drivers_license") {
          return DL_SHOW.includes(categoryKey);
        }
        return !PASSPORT_HIDE.includes(categoryKey);
      },
    );

    return (
      <section className="res-card tpl-acc" aria-labelledby="tpl-vars-title">
        <div className="tpl-acc-head">
          <button
            type="button"
            className="tpl-acc-toggle"
            aria-expanded={showVariables}
            aria-controls="tpl-vars-body"
            onClick={() => setShowVariables(!showVariables)}
          >
            {head}
            <ChevronDown
              size={22}
              className="tpl-acc-chevron"
              aria-hidden="true"
            />
          </button>
          <label className="tpl-acc-filter">
            <span className="sr-only">Страна для переменных</span>
            <select
              className="rec-select tpl-select-sm"
              value={selectedCountryForVars}
              onChange={(e) => {
                e.stopPropagation();
                setSelectedCountryForVars(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <option value="">Все страны</option>
              {countryOptions}
            </select>
          </label>
        </div>

        {showVariables && (
          <div id="tpl-vars-body" className="tpl-acc-body">
            <p className="tpl-lead">
              Используйте эти переменные в ваших .docx шаблонах. Формат:{" "}
              <code className="tpl-inline-code">{"{{ имяПеременной }}"}</code>
            </p>

            <div className="tpl-var-grid">
              {filteredVariables.map(([categoryKey, category]) => (
                <div key={categoryKey} className="tpl-var-group">
                  <h3 className="tpl-group-title">{category.name}</h3>
                  <div className="tpl-var-list">
                    {category.fields.map((field) => (
                      <div key={field.key}>
                        {placeholderRow({
                          id: `var_${field.key}`,
                          code: `{{ ${field.key} }}`,
                          label: field.label,
                        })}

                        {field.has_parts && (
                          <div className="tpl-var-parts">
                            {field.parts.map((part) =>
                              placeholderRow({
                                id: `var_${part.key}`,
                                code: `{{ ${part.key} }}`,
                                label: part.label,
                              }),
                            )}
                          </div>
                        )}

                        {field.is_country_specific && (
                          <p className="tpl-var-note">
                            <Globe size={13} aria-hidden="true" />
                            Только для: {countryName(field.country_code)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {documentType === "passport" && (
                <div className="tpl-var-group">
                  <h3 className="tpl-group-title">
                    Страницы паспорта (визы, штампы)
                  </h3>
                  <p className="tpl-muted tpl-group-desc">
                    Используйте для вывода информации со страниц паспорта (визы,
                    штампы, регистрации). Формат:{" "}
                    <code className="tpl-inline-code">{"{{ page04 }}"}</code>
                  </p>
                  <div className="tpl-var-list">
                    {[
                      "03",
                      "04",
                      "05",
                      "06",
                      "07",
                      "08",
                      "09",
                      "10",
                      "11",
                      "12",
                      "13",
                      "14",
                      "15",
                    ].map((num) =>
                      placeholderRow({
                        id: `page${num}`,
                        code: `{{ page${num} }}`,
                        label: `Информация со страницы ${parseInt(num)}`,
                      }),
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    );
  };

  const visibleTemplates = templates
    .filter(
      (t) =>
        (!filterCountry || t.country_code === filterCountry) &&
        (!filterDocType || (t.document_type || "passport") === filterDocType),
    )
    .sort((a, b) => a.label.localeCompare(b.label, "ru"));

  const colCount = user?.is_admin ? 9 : 8;

  return (
    <>
      <PageHeader
        title="Управление шаблонами"
        subtitle="Word-шаблоны (.docx), по которым собираются готовые переводы"
      >
        <button
          type="button"
          onClick={() => navigate("/template-editor")}
          className="px-btn px-btn-ink tpl-editor-btn"
        >
          <Code size={20} aria-hidden="true" />
          Открыть редактор шаблонов
        </button>
      </PageHeader>

      <div className="tpl-page">
        <section className="res-card tpl-upload" aria-labelledby="tpl-upload-title">
          <div className="res-card-head">
            <p className="res-eyebrow">Новый шаблон</p>
            <h2 id="tpl-upload-title" className="res-card-title">
              Добавить новый шаблон
            </h2>
          </div>

          <form onSubmit={handleUpload} className="tpl-form">
            <div className="tpl-form-grid">
              <label className="rec-field">
                <span className="rec-label">Страна</span>
                <select
                  className="rec-select"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                >
                  {countryOptions}
                </select>
              </label>

              <label className="rec-field">
                <span className="rec-label">Тип документа</span>
                <select
                  className="rec-select"
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                >
                  {docTypeOptions("form")}
                </select>
              </label>

              <label className="rec-field tpl-field-wide">
                <span className="rec-label">Название (для списка)</span>
                <input
                  className="tpl-input"
                  type="text"
                  placeholder="Например: Для банка (Душанбе)"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </label>

              <label className="rec-field">
                <span className="rec-label">Формат дат</span>
                <select
                  className="rec-select"
                  value={dateFormat}
                  onChange={(e) => setDateFormat(e.target.value)}
                >
                  <option value="DD.MM.YYYY">31.12.2000</option>
                  <option value="DD/MM/YYYY">31/12/2000</option>
                </select>
              </label>
            </div>

            <div className="tpl-form-foot">
              <label className={`tpl-file${uploadFile ? " has-file" : ""}`}>
                <input
                  type="file"
                  accept=".docx"
                  className="sr-only"
                  onChange={handleFileChange}
                />
                <span className="tpl-file-icon" aria-hidden="true">
                  {uploadFile ? <Check size={22} /> : <FileUp size={22} />}
                </span>
                <span className="tpl-file-text">
                  <span className="tpl-file-title">
                    {uploadFile ? uploadFile.name : "Выберите файл (.docx)"}
                  </span>
                  <span className="tpl-file-sub">
                    {uploadFile
                      ? "Файл выбран — нажмите, чтобы заменить"
                      : "Word-документ с плейсхолдерами {{ … }}"}
                  </span>
                </span>
              </label>

              {user.is_admin ? (
                <label className="rec-checkbox tpl-public">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                  />
                  Публичный
                </label>
              ) : null}

              <button
                type="submit"
                className="px-btn px-btn-ink tpl-submit"
                disabled={uploading}
              >
                {uploading ? (
                  "Загрузка…"
                ) : (
                  <>
                    <Upload size={20} aria-hidden="true" /> Загрузить
                  </>
                )}
              </button>
            </div>

            {uploadError && (
              <p className="tpl-alert" role="alert">
                {uploadError}
              </p>
            )}
          </form>
        </section>

        <section className="tpl-list" aria-labelledby="tpl-list-title">
          <div className="tpl-list-head">
            <h2 id="tpl-list-title" className="px-section-title">
              Список шаблонов
              {!loading && templates.length > 0 && (
                <span className="tpl-count">{visibleTemplates.length}</span>
              )}
            </h2>

            <div className="tpl-toolbar">
              <label className="tpl-toolbar-field">
                <span className="sr-only">Фильтр по типу документа</span>
                <select
                  className="rec-select tpl-select-sm"
                  value={filterDocType}
                  onChange={(e) => setFilterDocType(e.target.value)}
                >
                  <option value="">Все типы</option>
                  {docTypeOptions("filter")}
                </select>
              </label>
              <label className="tpl-toolbar-field">
                <span className="sr-only">Фильтр по стране</span>
                <select
                  className="rec-select tpl-select-sm"
                  value={filterCountry}
                  onChange={(e) => setFilterCountry(e.target.value)}
                >
                  <option value="">Все страны</option>
                  {countryOptions}
                </select>
              </label>
              {filterCountry && (
                <button
                  type="button"
                  className="tpl-reset"
                  onClick={() => setFilterCountry("")}
                >
                  <X size={16} aria-hidden="true" />
                  Сбросить
                </button>
              )}
            </div>
          </div>

          {error && (
            <p className="tpl-alert" role="alert">
              {error}
            </p>
          )}

          {loading ? (
            <div className="px-empty">Загрузка списка…</div>
          ) : templates.length === 0 ? (
            <div className="px-empty">У вас пока нет загруженных шаблонов.</div>
          ) : (
            <div className="px-table-wrap">
              <table className="tpl-table">
                <thead>
                  <tr>
                    <th scope="col">Страна</th>
                    <th scope="col">Название</th>
                    {user?.is_admin && <th scope="col">Владелец</th>}
                    <th scope="col">Тип документа</th>
                    <th scope="col">Формат дат</th>
                    <th scope="col">Файл</th>
                    <th scope="col">Добавлен</th>
                    <th scope="col">Доступ</th>
                    <th scope="col">
                      <span className="sr-only">Действия</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTemplates.length === 0 && (
                    <tr>
                      <td colSpan={colCount} className="tpl-table-empty">
                        Ничего не найдено
                      </td>
                    </tr>
                  )}
                  {visibleTemplates.map((t) => {
                    const editing = editingId === t.id;
                    return (
                      <tr key={t.id} className={editing ? "is-editing" : ""}>
                        <td>
                          <span className="tpl-country">
                            {countryName(t.country_code)}
                          </span>
                        </td>
                        <td className="tpl-name-cell">
                          {editing ? (
                            <input
                              className="tpl-cell-input"
                              aria-label="Название шаблона"
                              value={editLabel}
                              onChange={(e) => setEditLabel(e.target.value)}
                              onKeyDown={(e) =>
                                e.key === "Enter" && handleSaveEdit(t.id)
                              }
                              autoFocus
                            />
                          ) : (
                            <span className="tpl-name" title={t.label}>
                              {t.label}
                            </span>
                          )}
                        </td>
                        {user?.is_admin && (
                          <td className="tpl-soft">
                            {t.is_own ? "Мой" : `ID: ${t.user_id || "—"}`}
                          </td>
                        )}
                        <td>
                          {editing ? (
                            <select
                              className="tpl-cell-input tpl-cell-select"
                              aria-label="Тип документа"
                              value={editDocType}
                              onChange={(e) => setEditDocType(e.target.value)}
                            >
                              {docTypeOptions("short")}
                            </select>
                          ) : (
                            <DocTypeBadge type={t.document_type} />
                          )}
                        </td>
                        <td>
                          {editing ? (
                            <select
                              className="tpl-cell-input tpl-cell-select"
                              aria-label="Формат дат"
                              value={editDateFormat}
                              onChange={(e) =>
                                setEditDateFormat(e.target.value)
                              }
                            >
                              <option value="DD.MM.YYYY">31.12.2000</option>
                              <option value="DD/MM/YYYY">31/12/2000</option>
                            </select>
                          ) : (
                            <span className="tpl-mono-chip">
                              {t.date_format || "DD.MM.YYYY"}
                            </span>
                          )}
                        </td>
                        <td className="tpl-file-cell">
                          <span className="tpl-filename">
                            <FileText size={16} aria-hidden="true" />
                            <span title={t.filename}>{t.filename}</span>
                          </span>
                        </td>
                        <td className="tpl-soft tpl-nowrap">
                          {new Date(t.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          {user?.is_admin ? (
                            <button
                              type="button"
                              className={`tpl-access${t.is_public ? " is-public" : ""}`}
                              onClick={() =>
                                handleTogglePublic(t.id, t.is_public)
                              }
                              title={
                                t.is_public
                                  ? "Сделать приватным"
                                  : "Сделать публичным"
                              }
                              aria-label={
                                t.is_public
                                  ? `Публичный. Сделать приватным: ${t.label}`
                                  : `Приватный. Сделать публичным: ${t.label}`
                              }
                            >
                              {t.is_public ? (
                                <Globe size={15} aria-hidden="true" />
                              ) : (
                                <Lock size={15} aria-hidden="true" />
                              )}
                              {t.is_public ? "Публичный" : "Приватный"}
                            </button>
                          ) : (
                            t.is_public && (
                              <span className="tpl-access is-public is-static">
                                <Globe size={15} aria-hidden="true" />
                                Публичный
                              </span>
                            )
                          )}
                        </td>
                        <td className="tpl-actions">
                          {(t.own || user?.is_admin) &&
                            (editing ? (
                              <>
                                <button
                                  type="button"
                                  className="px-icon-btn is-small tpl-save"
                                  onClick={() => handleSaveEdit(t.id)}
                                  title="Сохранить"
                                  aria-label="Сохранить изменения"
                                >
                                  <Check size={18} aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="px-icon-btn is-small tpl-ghost"
                                  onClick={() => setEditingId(null)}
                                  title="Отменить"
                                  aria-label="Отменить изменения"
                                >
                                  <X size={18} aria-hidden="true" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="px-icon-btn is-small tpl-ghost"
                                  onClick={() => handleEdit(t)}
                                  title="Изменить"
                                  aria-label={`Изменить: ${t.label}`}
                                >
                                  <Edit2 size={17} aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="px-icon-btn is-small tpl-ghost tpl-delete"
                                  onClick={() => handleDelete(t.id)}
                                  title="Удалить"
                                  aria-label={`Удалить: ${t.label}`}
                                >
                                  <Trash2 size={17} aria-hidden="true" />
                                </button>
                              </>
                            ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {renderVariablesSection()}

        <section className="res-card tpl-acc" aria-labelledby="tpl-examples-title">
          <div className="tpl-acc-head">
            <button
              type="button"
              className="tpl-acc-toggle"
              aria-expanded={showInstructions}
              aria-controls="tpl-examples-body"
              onClick={() => setShowInstructions(!showInstructions)}
            >
              <span className="tpl-acc-icon tone-peach" aria-hidden="true">
                <FileText size={20} />
              </span>
              <span className="tpl-acc-text">
                <span className="res-eyebrow">Справка</span>
                <span id="tpl-examples-title" className="res-card-title">
                  Примеры использования
                </span>
              </span>
              <ChevronDown
                size={22}
                className="tpl-acc-chevron"
                aria-hidden="true"
              />
            </button>
          </div>

          {showInstructions && (
            <div id="tpl-examples-body" className="tpl-acc-body">
              <div className="tpl-examples">
                <div className="tpl-examples-col">
                  <div>
                    <h3 className="tpl-sub-title">
                      <PenLine size={18} aria-hidden="true" />
                      Как использовать
                    </h3>
                    <p className="tpl-muted tpl-group-desc">
                      Создайте .docx файл и вставьте плейсхолдеры в нужные
                      места. Текст получит тот же шрифт и стиль, что у
                      плейсхолдера.
                    </p>
                    <div className="tpl-var-list">
                      {placeholderRow({
                        id: "ex1",
                        code: "{{ surname }}",
                        label: "Фамилия",
                      })}
                      {placeholderRow({
                        id: "ex2",
                        code: "{{ givenNames }}",
                        label: "Имя",
                      })}
                      {placeholderRow({
                        id: "ex3",
                        code: "{{ documentNumber }}",
                        label: "Номер паспорта",
                      })}
                    </div>
                  </div>

                  <div>
                    <h3 className="tpl-sub-title">
                      <CalendarDays size={18} aria-hidden="true" />
                      Части дат
                    </h3>
                    <p className="tpl-muted tpl-group-desc">
                      Работает для birthDate, issueDate, expiryDate:
                    </p>
                    <div className="tpl-var-list">
                      {placeholderRow({
                        id: "d1",
                        code: "{{ birthDate__day }}",
                        label: "День",
                      })}
                      {placeholderRow({
                        id: "d2",
                        code: "{{ birthDate__month }}",
                        label: "Месяц",
                      })}
                      {placeholderRow({
                        id: "d3",
                        code: "{{ birthDate__year }}",
                        label: "Год",
                      })}
                    </div>
                  </div>
                </div>

                <div className="tpl-examples-col">
                  <div>
                    <h3 className="tpl-sub-title">
                      <CaseSensitive size={18} aria-hidden="true" />
                      Управление регистром
                    </h3>
                    <div className="tpl-cases">
                      {[
                        {
                          code: "{{ surname|upper }}",
                          desc: "ВСЕ ЗАГЛАВНЫЕ",
                          id: "u1",
                        },
                        {
                          code: "{{ surname|capitalize }}",
                          desc: "Первая Заглавная",
                          id: "u2",
                        },
                        {
                          code: "{{ surname|lower }}",
                          desc: "все строчные",
                          id: "u3",
                        },
                      ].map((item) => (
                        <div key={item.id} className="tpl-case">
                          <div className="tpl-case-text">
                            <code>{item.code}</code>
                            <span>{item.desc}</span>
                          </div>
                          {copyButton(item.code, item.id)}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="tpl-sub-title">
                      <FileText size={18} aria-hidden="true" />
                      Страницы паспорта (визы/штампы)
                    </h3>
                    <p className="tpl-muted tpl-group-desc">
                      Плейсхолдер страницы автоматически заполнится данными визы
                      или штампа с этой страницы. Если данных нет — «Нет
                      сведений».
                    </p>
                    <div className="tpl-page-chips">
                      {["04", "05", "06", "07", "08", "09", "10"].map((num) => {
                        const tag = `{{ page${num} }}`;
                        return (
                          <span key={num} className="tpl-var-code">
                            <code>{tag}</code>
                            {copyButton(tag, `cp${num}`, 14)}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  <p className="tpl-tip">
                    <Lightbulb size={18} aria-hidden="true" />
                    <span>
                      <strong>Совет:</strong> используйте редактор шаблонов для
                      удобной вставки переменных прямо в документ.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default TemplatesPage;
